import { createRef, Component } from "react";
import ReactDOMServer from "react-dom/server";

import { toast } from "extensions/toast";
import { captureException } from "extensions/sentry";

import classNames from "classnames";

import { SCHEDULE_AUTOMATIC_DOWNLOAD } from "common/reducers/downloads";

import { emitter } from "common/emitter";
import { store } from "common/store";
import { request } from "common/components/request";
import { ErrorView } from "common/components/error";
import { CheckBox } from "common/components/checkbox";
import { OptionsButton } from "common/components/buttons/optionsButton";
import { PrimaryButton, CancelButton } from "common/components/buttons";

import { Webix } from "common/components/webix/react/webix";
import { getters } from "common/components/getters/getters";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/pro-light-svg-icons";

const submitExportRequest = function(payload, shouldStartDownloadAutomatically) {
    const _payload = {
        filters: payload.filters || {},
        ...payload
    };

    // At this point we can convert qualifiers to encoded string
    if (Object.prototype.hasOwnProperty.call(_payload.filters, "q")) {
        _payload.filters.q = _payload.filters.q.toString(true);
    }

    request("/merchant/:merchant_ref/user/export", {
        method: "POST",
        data: _payload
    }).then((data) => {
        if (shouldStartDownloadAutomatically) {
            store.dispatch(SCHEDULE_AUTOMATIC_DOWNLOAD(data.job_ref));
        }

        const showNotifications = (e) => {
            e.preventDefault();
            emitter.emit("open.downloadsPanel");
        };
        toast.success((
            <div>Your file will appear in the <strong><FontAwesomeIcon icon={faBell}/> <a className="alert-link" onClick={showNotifications} href="#">notifications panel</a></strong> when ready for download</div>
        ));
    }).catch((error) => {
        toast.error("Unable to create a report");
        captureException(error);
    });
};

class ExportDialog extends Component {
    constructor(props) {
        super(props);
        this.list_ref = createRef();
        this.state = {
            config: { data: [] },
            fileName: this.props.exportOptions.suggested_name,
            fileFormat: "csv",
            automaticDownload: false,
            sortOrder: "config",
            hasError: false,
            exportAll: false
        };
        this.ui = {
            view: "list",
            minHeight: 300,
            minWidth: 500,
            autowidth: true,
            select: false,
            scrollY: false,
            template: (obj) => ReactDOMServer.renderToStaticMarkup(
                <CheckBox className="export-list-checkbox" label={obj.header} value={obj.will_export} onChange={() => {}} name={toString(obj.id)} />
            ),
            adjust: true,
            on: {
                onItemClick: this.toggleVisibility
            }
        };
    }

    componentDidMount() {
        const { data } = this.props.config;
        this.setState({
            config: {
                ...this.props.config,
                data: data.map((item, index) => ({ weight: index, ...item, will_export: item.collapsed_visible }))
            }
        });
    }

    getSnapshotBeforeUpdate() {
        const node = this.list_ref.current.getWebixElement().getNode();
        return {
            listScrollTop: node.scrollTop
        };
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!this.state.hasError) {
            const list = this.list_ref.current.getWebixElement();
            list.refresh();
            if (snapshot !== null) {
                list.getNode().scrollTop = snapshot.listScrollTop;
            }
        }
    }

    componentWillUnmount() {
        this.list_ref = null;
    }

    toggleVisibility = (id, e, node) => {
        const getter = this.list_ref.current.ui.getItem(id);
        this.setState(
            (prevState) => {
                const data = prevState.config.data
                    .map((item) => (item.getter_ref === getter._id ? { ...item, will_export: !item.will_export } : { ...item }));
                return { config: { ...prevState.config, data } };
            }
        );
    };

    handleExportAll = (e) => {
        this.handleChange(e);
        this.setState(
            (prevState) => ({
                config: {
                    ...prevState.config,
                    data: prevState.config.data.map((item) => ({ ...item, will_export: e.target.value }))
                }
            })
        );
    };

    setSortOrder = (sortOrder) => () => this.setState({ sortOrder });

    handleExport = () => {
        try {
            const payload = this.prepareExport();
            submitExportRequest(payload, this.state.automaticDownload);
            this.props.close();
        } catch (error) {
            this.setState({ hasError: true, error });
        }
    };

    prepareExport = () => {
        const { filters, resource } = this.props.exportOptions;
        let section;
        if (this.props.section === "all_fraud") {
            section = "fraud";
        } else if (this.props.section === "psd2_fraud") {
            section = "psd2";
        } else {
            section = this.props.section;
        }

        return {
            name: this.state.fileName,
            content_type: this.state.fileFormat,
            content: [
                {
                    value: section,
                    structure: {
                        values: this.state.config.data.reduce((memo, { getter_ref, will_export }) => (will_export ? [...memo, getter_ref] : memo), [])
                    }
                }
            ],
            filters,
            resource
        };
    };

    handleChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        });
    };

    handleFormatChange = (fileFormat) => this.setState({ fileFormat });

    renderActions = () => (
        <div className="actions">
            <div className="group" />
            <div className="group">

                <CancelButton type="button" onClick={this.props.close}>Cancel</CancelButton>
                <PrimaryButton type="button" onClick={this.handleExport}>Export</PrimaryButton>
            </div>
        </div>
    );

    render() {
        if (this.state.hasError) {
            return (
                <div className="configurable-table-export table-export">
                    <ErrorView
                        error={this.state.error}
                        header="Export failed"
                        message="Something went wrong during the export. Try to refresh page."
                        hideDetails
                        captureInSentry
                        custom
                    />
                </div>
            );
        }

        const columns_list = this.state.config.data
            .reduce((memo, item, index) => [...memo, { ...getters.findById(item.getter_ref), weight: index, ...item }], [])
            .map((item) => ({ ...item, header: item.name }))
            .sort((a, b) => {
                if (this.state.sortOrder === "config") {
                    return a.weight > b.weight ? 1 : -1;
                }
                return a.header > b.header ? 1 : -1;
            });
        const formats = this.props.exportOptions.formats.map((format) => ({ ...format, onClick: this.handleFormatChange })) || [];

        return (
            <div className="configurable-table-export table-export">
                <div className="file-name">
                    <div className="field-name">File name</div>
                    <input type="text" className="form-control fileName" name="fileName" value={this.state.fileName} onChange={this.handleChange} />
                </div>
                <div className="format-options">
                    <div className="field-name">File format</div>
                    <OptionsButton structure={formats} activeValue={this.state.fileFormat} />
                </div>
                <div className="list-header">
                    <CheckBox label="Select all" onChange={this.handleExportAll} name="exportAll" value={this.state.exportAll} />
                    <div className="sort_order">
                        Sort by:
                        <span className={classNames(["sort-button", { active: this.state.sortOrder === "config" }])} onClick={this.setSortOrder("config")}>Export order</span>|
                        <span className={classNames(["sort-button", { active: this.state.sortOrder === "az" }])} onClick={this.setSortOrder("az")}>A-Z</span>
                    </div>
                </div>
                <Webix className="config-columns bottom-bordered" ui={this.ui} data={columns_list} ref={this.list_ref} />
                {this.props.haveAutomaticDownload && <CheckBox label="Automatically download when ready" name="automaticDownload" onChange={this.handleChange} value={this.state.automaticDownload} />}
                {this.renderActions()}
            </div>
        );
    }
}

export { ExportDialog };
